import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import PropertyFieldsSection from "./PropertyFieldsSection"

const LABEL    = "block text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1"
const INPUT    = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"
const TEXTAREA = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"

const SearchField = ({ label, value, search, onPick }) => {
  const [query, setQuery]     = useState("")
  const [open, setOpen]       = useState(false)
  const [results, setResults] = useState([])
  const wrapRef     = useRef(null)
  const debounceRef = useRef(null)
  const requestRef  = useRef(0)

  useEffect(() => {
    const close = event => {
      if (wrapRef.current?.contains(event.target)) return
      setOpen(false)
      setQuery("")
    }
    document.addEventListener("click", close, true)
    return () => {
      document.removeEventListener("click", close, true)
      clearTimeout(debounceRef.current)
    }
  }, [])

  const run = term => {
    const request = requestRef.current += 1
    return Promise.resolve(search(term))
      .then(found => { if (request === requestRef.current) setResults(found || []) })
      .catch(() => { if (request === requestRef.current) setResults([]) })
  }

  const type = event => {
    setQuery(event.target.value)
    setOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => run(event.target.value), 250)
  }

  const pick = item => {
    onPick(item)
    setOpen(false)
    setQuery("")
  }

  return (
    <div ref={wrapRef} className="relative">
      <label className={LABEL}>{label}</label>
      <div className="relative">
        <FontAwesomeIcon icon="magnifying-glass" className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400" />
        <input
          className   = {`${INPUT} pl-8`}
          value       = {open ? query : (value?.name || "")}
          placeholder = {value?.name || ""}
          onFocus     = {() => { setOpen(true); setQuery(""); run("") }}
          onChange    = {type}
        />
      </div>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">{I18n.t("quotes.project_index.duplicate.no_match")}</div>
          ) : results.map(item => (
            <button key={item.id} type="button" onClick={() => pick(item)} className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-50">
              {item.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const Check = ({ label, checked, onChange }) => (
  <label className="inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
    <input type="checkbox" className="h-4 w-4 rounded border border-slate-300" checked={!!checked} onChange={event => onChange(event.target.checked)} />
    {label}
  </label>
)

const Header = ({
  project, client, onClientChange, searchClients, site, onSiteChange, searchSites, backRoute, header, onHeaderChange, minDate, heading,
  billingCategories = [], billingCategoryOnLine, billingCategoryMandatory,
  categoriesEnabled, categories = [], categoryMandatory,
  remindersAvailable, onlinePaymentAvailable,
  propertyFields = [], propertyValues = {}, onPropertyChange,
}) => (
  <div className="px-8 pt-6 pb-6 bg-white border-b border-slate-200">
    <div className="flex items-center gap-2 text-[12.5px] text-slate-500 mb-3">
      <a href={backRoute} className="hover:text-slate-900 inline-flex items-center gap-1.5">
        <FontAwesomeIcon icon="arrow-left" />
        {I18n.t("projects.invoices.builder.back_to_project")}
      </a>
      <span>·</span>
      <span className="text-slate-700">{project.name}</span>
      {project.code && <span className="text-slate-400 tabular-nums">{project.code}</span>}
    </div>

    <h1 className="text-2xl font-semibold text-slate-900 m-0 mb-5">
      {heading || I18n.t("projects.invoices.builder.title")}
    </h1>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <SearchField label={I18n.t("projects.invoices.builder.client")} value={client} search={searchClients} onPick={onClientChange} />
      <SearchField label={I18n.t("projects.invoices.builder.site")}   value={site}   search={searchSites}   onPick={onSiteChange} />
      <div>
        <label className={LABEL}>{I18n.t("projects.invoices.builder.invoice_title")}</label>
        <input className={INPUT} value={header.title} onChange={event => onHeaderChange({ title: event.target.value })} />
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
      <div>
        <label className={LABEL}>{I18n.t("projects.invoices.builder.creation_date")}</label>
        <input type="date" min={minDate || undefined} className={INPUT} value={header.creation_date || ""} onChange={event => onHeaderChange({ creation_date: event.target.value })} />
      </div>
      <div>
        <label className={LABEL}>{I18n.t("projects.invoices.builder.payment_delay")}</label>
        <input type="number" min="0" className={INPUT} value={header.payment_delay ?? ""} onChange={event => onHeaderChange({ payment_delay: event.target.value })} />
      </div>
      <div>
        <label className={LABEL}>{I18n.t("invoices.client_reference")}</label>
        <input className={INPUT} value={header.client_reference || ""} onChange={event => onHeaderChange({ client_reference: event.target.value })} />
      </div>
      {!billingCategoryOnLine && billingCategories.length > 0 && (
        <div>
          <label className={LABEL}>
            {I18n.t("billing_categories.billing_category_short")}
            {billingCategoryMandatory && <span className="text-rose-500"> *</span>}
          </label>
          <select className={`${INPUT} bg-white`} value={header.billing_category_id || ""} onChange={event => onHeaderChange({ billing_category_id: event.target.value })}>
            <option value="">{I18n.t("billing_categories.select")}</option>
            {billingCategories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </div>
      )}
      {categoriesEnabled && categories.length > 0 && (
        <div>
          <label className={LABEL}>
            {I18n.t("categories.category")}
            {categoryMandatory && <span className="text-rose-500"> *</span>}
          </label>
          <select className={`${INPUT} bg-white`} value={header.category_id || ""} onChange={event => onHeaderChange({ category_id: event.target.value })}>
            <option value="">{I18n.t("categories.select")}</option>
            {categories.map(category => <option key={category.id} value={category.id}>{category.parent_id ? `— ${category.name}` : category.name}</option>)}
          </select>
        </div>
      )}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <div>
        <label className={LABEL}>{I18n.t("invoices.description")}</label>
        <textarea rows={2} className={TEXTAREA} value={header.description || ""} onChange={event => onHeaderChange({ description: event.target.value })} />
      </div>
      <div>
        <label className={LABEL}>{I18n.t("quotes.new.internal_info")}</label>
        <textarea rows={2} className={TEXTAREA} value={header.internal_info || ""} onChange={event => onHeaderChange({ internal_info: event.target.value })} />
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
      <Check label={I18n.t("projects.invoices.builder.end_of_month")} checked={header.end_of_month} onChange={value => onHeaderChange({ end_of_month: value })} />
      <Check label={I18n.t("invoices.pdf_preferences.show_workorder_address")} checked={header.display_int_address} onChange={value => onHeaderChange({ display_int_address: value })} />
      {remindersAvailable && (
        <Check label={I18n.t("reminders.title")} checked={header.reminders_enabled} onChange={value => onHeaderChange({ reminders_enabled: value })} />
      )}
      {onlinePaymentAvailable && (
        <Check label={I18n.t("invoices.payment_links.title")} checked={header.should_enable_payment_link} onChange={value => onHeaderChange({ should_enable_payment_link: value })} />
      )}
    </div>

    {propertyFields.length > 0 && (
      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-3">
          {I18n.t("projects.invoices.builder.additional_info")}
        </div>
        <PropertyFieldsSection fields={propertyFields} values={propertyValues} onChange={onPropertyChange} />
      </div>
    )}
  </div>
)

export default Header
